import { Injectable } from '@angular/core';
import { PendingReceipt, PendingReceiptProductType, PendingReceiptRODetail,
  ReceiptPaymentErrorTranslationKey, ReceiptPaymentErrorType, ReceiptPaymentServiceError
} from '@models/receipt';
import { RECEIPT_PAYMENT_LABEL, AMAZON_PAY_APP, AMAZON_PAY_ORIGIN, APP_IDS_PRIVATE, APP_IDS_COMPANY, DEFAULT, LABEL_TRUE,
  RECEIPT_PAYMENT_DEPARTMENT_DATA, RECEIPT_PAYMENT_COMPANY_DEPARTMENT_DATA, RECEIPT_DESCRIP_GENERIC, CENTRAL_TIMEZONE
} from '@modules/receipt-payment/receipt-payment-constants';
import { TranslateService } from '@ngx-translate/core';
import { AuthMirrorService } from '../core/auth/auth-mirror.service';
import { ContextService } from '../core/context.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import * as timezone from 'moment-timezone';

@Injectable({
  providedIn: 'root'
})
export class ReceiptPaymentHelperService {

  constructor(
    private mmobileHelper: MMobileHelperService,
    private ctx: ContextService,
    private authMirror: AuthMirrorService,
    private trans: TranslateService
  ) {}

  getAppIds() {
    let amazonPayPortal;
    if (this.ctx.isAccessTypeBusiness) {
      amazonPayPortal = 18;
    }

    if (this.ctx.isAccessTypeParticular) {
      amazonPayPortal = 11;
    }

    return {
      appId: this.getAppId(),
      amazonPayAppId: this.getAmazonPayAppId(),
      amazonPayOrigin: this.getAmazonPayOrigin(),
      amazonPayPortal
    };
  }

  getRemissionAppId(receipt: PendingReceipt) {
    return receipt.remissionAppId ? receipt.remissionAppId : '';
  }

  getServiceErrorObject(
    err: any,
    defaultErrorKey?: ReceiptPaymentErrorType
  ): any {
    const errKey = this.resolveReceiptPaymentServiceError(err, defaultErrorKey);
    return {
      locallyGeneratedError: true,
      errorKey: errKey,
      errorTranslationKey: this.resolveReceiptPaymentErrorTranslationKey(errKey)
    };
  }

  getServiceErrorObjectByErrorKey(errKey: any): any {
    return {
      locallyGeneratedError: true,
      errorKey: errKey,
      errorTranslationKey: this.resolveReceiptPaymentErrorTranslationKey(errKey)
    };
  }

  resolveReceiptPaymentErrorTranslationKey(
    errorType: ReceiptPaymentErrorType
  ): ReceiptPaymentErrorTranslationKey {
    if (errorType && ReceiptPaymentErrorTranslationKey[errorType]) {
      return ReceiptPaymentErrorTranslationKey[errorType];
    } else {
      return ReceiptPaymentErrorTranslationKey.DEFAULT;
    }
  }


  resolveReceiptPaymentServiceError(
    serviceError: any,
    defaultErrorKey: ReceiptPaymentErrorType
  ): ReceiptPaymentErrorType {
    let errorType;
    if (this.checkIsServiceDefinedError(serviceError)) {
      errorType = Object.keys(ReceiptPaymentServiceError).find(
        el => ReceiptPaymentServiceError[el] === serviceError.error.errorKey
      );
    }
    if (this.checkIsSearchUserServiceError(serviceError)) {
      errorType = Object.keys(ReceiptPaymentServiceError).find(
        el =>
          ReceiptPaymentServiceError[el] ===
          serviceError.error.extraData.errorKey
      );
    }
    if (errorType) {
      return errorType as ReceiptPaymentErrorType;
    } else {
      if (defaultErrorKey) {
        return defaultErrorKey;
      } else {
        return DEFAULT.toUpperCase() as ReceiptPaymentErrorType;
      }
    }
  }

  setReceiptDepartmentInfoFromMMobile(receipt: PendingReceipt): PendingReceipt {
    if (!receipt) {
      return null;
    }
    let departmentType = null;
    if (receipt.type === PendingReceiptProductType.PAYMENT_PLAN_FEE) {
      departmentType = PendingReceiptProductType.PAYMENT_PLAN_FEE;
    } else {
      departmentType = this.getDepartmentTypeFromMMobile(
        receipt.policyCompanyId,
        receipt.policyDepartment
      );
    }
    if (!departmentType) {
      return null;
    }
    const departmentData = this.getReceiptPaymentDepartmentData(departmentType);
    receipt.phoneNumbers = departmentData.phoneNumbers;
    receipt.departmentName = departmentData.departmentName;
    receipt.redsysAgrup = departmentData.redsysAgrup;
    receipt.remissionAppId = departmentData.remissionAppId;
    receipt.departmentType = departmentType;
    receipt.description = this.getTranslatedReceiptDescription(receipt);
    const roDetail: PendingReceiptRODetail = {
      receiptNumber: receipt.number,
      documentNumber:
        receipt.payerDocumentNumber != null
          ? receipt.payerDocumentNumber.toUpperCase()
          : null,
      fullName: receipt.payerFullName,
      carPlate: receipt.carPlate,
      amount: receipt.amount,
      effectDate: receipt.effectDate,
      expirationDate: receipt.expirationDate,
      risk: receipt.description
    };
    if (departmentType === PendingReceiptProductType.PAYMENT_PLAN_FEE) {
      roDetail.paymentPlanId = receipt.paymentPlanId;
      roDetail.belongsToPaymentsPlan = LABEL_TRUE;
    } else {
      roDetail.policyNumber = receipt.policyNumber;
    }
    receipt.roDetailString = JSON.stringify(roDetail);
    return receipt;
  }

  getDepartmentTypeFromMMobile(policyCompanyId: string, policyDepartment: string) {
    const receiptPaymentDepartmentData = this.getReceiptPaymentDepartmentData();

    if (!receiptPaymentDepartmentData) {
      return '';
    }

    const matches = Object.entries(receiptPaymentDepartmentData as { [key: string]: any })
      .filter(
        ([_, departmentType]) =>
          (departmentType.companyIds || []).some(v => v === policyCompanyId)
      )
      .filter(
        ([key, departmentType]) =>
          key.startsWith('GENERIC') ||
          (departmentType.departments || []).some(v => v === policyDepartment)
      );
    const nonGenericMatch = matches.find(([key, _]) => !key.startsWith('GENERIC'));
    const genericMatch = matches.find(([key, _]) => key.startsWith('GENERIC'));

    return (nonGenericMatch || genericMatch || [''])[0];
  }

  getTranslatedReceiptDescription(receipt: PendingReceipt): string {
    if (!receipt) {
      return '';
    }

    const interpolateParams = {
      policyNumber: receipt.policyNumber || '',
      carModel: receipt.carModel || '',
      riskPostalCode: receipt.riskPostalCode || '',
      paymentPlanId: receipt.paymentPlanId || ''
    };
    const { riskDescription } = this.getReceiptPaymentDepartmentData(receipt.departmentType) || ({} as any);
    return riskDescription
      ? this.interpolate(riskDescription, interpolateParams)
      : this.interpolate(RECEIPT_DESCRIP_GENERIC, interpolateParams);
  }


  checkLifeServicesAreAvailable() {
    const centralCurrentTime = timezone.tz(CENTRAL_TIMEZONE);
    const weekday = centralCurrentTime.isoWeekday();
    // ISO Weektime in moment has 1-7 range
    const currentInactivitySchedule = this.mmobileHelper.getCustomConfigData(
      'RECEIPT_PAYMENT',
      'LIFE_SERVICES_INACTIVITY_SCHEDULE_MADRID_TIMEZONE',
      `${weekday}`
    );
    if (currentInactivitySchedule && currentInactivitySchedule.length) {
      return !this.timeIsInAnyInterval(
        centralCurrentTime,
        currentInactivitySchedule
      );
    }
    // Service is active if no Inactivity Schedule is found
    return true;
  }

  private timeIsInAnyInterval(time: any, schedule: any[]) {
    return schedule.find(interval => {
      const intervalStartTime = timezone.tz(
        interval.start,
        'HH:mm:ss',
        CENTRAL_TIMEZONE
      );
      const intervalEndTime = timezone.tz(
        interval.end,
        'HH:mm:ss',
        CENTRAL_TIMEZONE
      );
      if (intervalStartTime && intervalEndTime) {
        return time.isBetween(intervalStartTime, intervalEndTime);
      }
      return false;
    });
  }

  private interpolate(text, interpolateParams) {
    this.trans.set('MMOBILE_TEMP', text);
    return this.trans.instant('MMOBILE_TEMP', interpolateParams) as string;
  }

  private getReceiptPaymentDepartmentData(type?: string) {
    const mmobileKey = this.authMirror.authService.checkUserIsParticular() ?
      RECEIPT_PAYMENT_DEPARTMENT_DATA : RECEIPT_PAYMENT_COMPANY_DEPARTMENT_DATA;
    if (type) {
      return this.mmobileHelper.getCustomConfigData(mmobileKey, type);
    }
    return this.mmobileHelper.getCustomConfigData(mmobileKey);
  }




  private checkIsServiceDefinedError(serviceError: any): any {
    return serviceError && serviceError.error && serviceError.error.errorKey;
  }

  private checkIsSearchUserServiceError(serviceError: any): any {
    return (
      serviceError &&
      serviceError.error &&
      serviceError.error.extraData &&
      serviceError.error.extraData.errorKey
    );
  }


  private getAppId() {
    return this.getAmazonPayAppId();
  }

  private getAmazonPayAppId() {
    const appIdKey = this.amazonAppIdKey();
    return this.mmobileHelper.getCustomConfigData(RECEIPT_PAYMENT_LABEL, appIdKey, AMAZON_PAY_APP) || '';
  }

  private getAmazonPayOrigin() {
    const appIdKey = this.amazonAppIdKey();
    return this.mmobileHelper.getCustomConfigData(RECEIPT_PAYMENT_LABEL, appIdKey, AMAZON_PAY_ORIGIN) || '';
  }

  private amazonAppIdKey() {
    if (this.authMirror.authService.checkUserIsParticular()) {
      return APP_IDS_PRIVATE;
    }
    return APP_IDS_COMPANY;
  }
}
